/**
 * @lines98/client/lib/gnome-unlock-badge — ЧИСТАЯ логика бейджа «есть новые помощники»
 * на пункте рейла + per-player «last-seen granted» (тикет helper-unlock-notification-and-
 * rename, группа A2: AC-9/10/14 / I-Gn4).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §3 «правило-предикат → модуль, один источник»;
 * owner-directive 2026-05-30/06-17). «Есть новое» = server-truth `gnomeUnlocksGranted`
 * (сколько вех открытия выдано) > last-seen-granted (подтверждённый последним заходом на
 * экран «Помощники»). Это сравнение — ОДНО на проект: рейл (выставить dot) и экран
 * «Помощники» (сбросить last-seen при mount) читают ЕГО, не дублируя сравнение по файлам
 * (как `gnome-state.newlyUnlockedColors`). Бейдж — чистый UX-индикатор «загляни»; он НЕ
 * влияет на мету/добычу/ранг (display-only, I-Gn1).
 *
 * **server-truth (AC-9 / I-Gn4):** granted приходит ТОЛЬКО из меты (`gnomeUnlocksGranted`,
 * `lib/meta-snapshot` / `GET /meta/state`). Этот модуль НЕ источник granted — он лишь
 * сравнивает два числа (defensive к NaN/откату) и хранит last-seen.
 *
 * **last-seen через снимок-ствол (AC-14, НЕ ad-hoc localStorage-ключ):** per-player
 * хранилище — ветка общего `lib/screen-snapshot` (`createScreenSnapshot<number>`), та же
 * фабрика, что `meta-snapshot`/`leaderboard-snapshot`: per-player изоляция / синхронный
 * read / версионирование — в стволе, здесь только тип payload (число granted). НИКАКОГО
 * прямого `localStorage.setItem` под last-seen (анти-дубль-тест это ловит).
 *
 * SCOPE: предикат `hasUnseenUnlocks` + per-player last-seen (load/save). NOT IN SCOPE:
 * рендер dot'а (рейл-API `nav-rail.setItemBadge` + `.nav-item-badge--dot` CSS); reveal
 * открытия (`lib/gnome-reveal`); источник granted (`lib/meta-snapshot`).
 *
 * Cross-references:
 *   - lib/screen-snapshot.ts (ствол per-player снимка — load/save/clear, изоляция).
 *   - lib/meta-snapshot.ts (`gnomeUnlocksGranted` — server-truth granted).
 *   - lib/nav-rail.ts (`setItemBadge` — dot через рейл-API; consumer бейджа).
 *   - screens/gnomes-screen.ts (сброс last-seen := granted при mount, AC-10).
 */

import { createScreenSnapshot } from './screen-snapshot.js';

/**
 * Есть ли непросмотренные открытия помощников (бейдж виден?) — server-truth granted
 * больше подтверждённого заходом last-seen (AC-9). Defensive: на NaN/нечисловом/
 * отрицательном входе → `false` (бейдж не светится на мусоре). Откат (granted < last-seen,
 * рассинхрон) → `false` (бейдж не «воскресает» — granted монотонно растёт, §2.0). Чистая
 * функция (юнит-тестируема без DOM).
 *
 * @param granted  — server-truth число выданных вех (`gnomeUnlocksGranted`, 0..7).
 * @param lastSeen — последнее подтверждённое заходом на экран значение granted.
 */
export function hasUnseenUnlocks(granted: number, lastSeen: number): boolean {
  if (!Number.isFinite(granted) || !Number.isFinite(lastSeen)) return false;
  if (granted < 0 || lastSeen < 0) return false;
  return granted > lastSeen;
}

// =============================================================================
// last-seen granted — per-player (ветка снимка-ствола, AC-14)
// =============================================================================

/** Префикс ключа last-seen; полный ключ = `${PREFIX}${playerId}` (изоляция по игроку). */
const KEY_PREFIX = 'lines98.gnome.unlock.lastSeen.v1.';
/** Версия payload — last-seen = просто число granted (несовпадение → отбрасываем). */
const SCHEMA_VERSION = 1;

/** Ветка снимка: per-player last-seen granted (число). Persistence/изоляция — в стволе. */
const store = createScreenSnapshot<number>({
  keyPrefix: KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: (v): v is number => typeof v === 'number' && Number.isFinite(v),
});

/**
 * Прочитать last-seen granted ТЕКУЩЕГО игрока СИНХРОННО (для рейл-бейджа при mount).
 * Нет снимка / гость / битый → `0` (свежая сессия: любое granted>0 даёт бейдж, AC-9).
 */
export function loadLastSeenUnlocks(): number {
  return store.load() ?? 0;
}

/**
 * Записать last-seen := granted ТЕКУЩЕГО игрока (сброс бейджа при заходе на экран
 * «Помощники», AC-10). No-op для гостя (нет ключа). Defensive-persistence в стволе.
 */
export function saveLastSeenUnlocks(granted: number): void {
  if (!Number.isFinite(granted) || granted < 0) return;
  store.save(granted);
}
